import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from '../firebase.config';
import { auth, db } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { getSettingsDoc } from '../utils/appArtifacts';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const { changeLanguage, supportedLanguages } = useLanguage();
  
  // Account summary
  const [account, setAccount] = useState({ 
    uid: '', 
    email: '', 
    displayName: '', 
    emailVerified: false, 
    phoneNumber: '', 
    providers: [], 
    creationTime: '', 
    lastSignInTime: '', 
    photoURL: '' 
  });
  
  // Profile
  const [profile, setProfile] = useState({ 
    displayName: '', 
    email: '', 
    phone: '', 
    recoveryEmail: '', 
    secondaryPhone: '', 
    language: 'en-IN', 
    timezone: 'Asia/Kolkata', 
    currency: 'INR' 
  });
  const [profileMsg, setProfileMsg] = useState('');

  // Load account and profile data
  useEffect(() => {
    const loadData = async () => {
      if (!auth.currentUser) return;
      
      const user = auth.currentUser;
      setAccount({
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        emailVerified: user.emailVerified,
        phoneNumber: user.phoneNumber || '',
        providers: user.providerData.map(p => p.providerId),
        creationTime: user.metadata.creationTime,
        lastSignInTime: user.metadata.lastSignInTime,
        photoURL: user.photoURL || ''
      });

      // Load profile from main users collection first (where recoveryPhones is stored)
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          console.log('User data from main collection:', userData);
          console.log('RecoveryPhones from main collection:', userData.recoveryPhones);
          const secondaryPhoneValue = userData.secondaryPhone || userData.secondary_phone || userData.recoveryPhones?.[0] || '';
          console.log('Setting secondaryPhone from main collection to:', secondaryPhoneValue);
          setProfile({
            displayName: userData.displayName || userData.name || user.displayName || '',
            email: userData.email || user.email || '',
            phone: userData.phone || userData.phoneNumber || user.phoneNumber || '',
            recoveryEmail: userData.recoveryEmail || userData.recovery_email || '',
            secondaryPhone: secondaryPhoneValue,
            language: userData.language || 'en-IN',
            timezone: userData.timezone || 'Asia/Kolkata',
            currency: userData.currency || 'INR'
          });
        } else {
          console.log('No user document found in main collection, checking settings');
          // Try to get data from settings collection as fallback
          try {
            const settingsRef = getSettingsDoc(user.uid);
            const settingsSnap = await getDoc(settingsRef);
            if (settingsSnap.exists()) {
              const data = settingsSnap.data();
              console.log('Profile data from settings:', data);
              console.log('RecoveryPhones from settings:', data.recoveryPhones);
              const secondaryPhoneValue = data.secondaryPhone || data.secondary_phone || data.alternatePhone || data.recoveryPhones?.[0] || '';
              console.log('Setting secondaryPhone from settings to:', secondaryPhoneValue);
              setProfile({
                displayName: data.displayName || data.name || user.displayName || '',
                email: data.email || user.email || '',
                phone: data.phone || data.phoneNumber || data.primaryPhone || '',
                recoveryEmail: data.recoveryEmail || data.recovery_email || data.secondaryEmail || '',
                secondaryPhone: secondaryPhoneValue,
                language: data.language || data.preferredLanguage || 'en-IN',
                timezone: data.timezone || data.timeZone || 'Asia/Kolkata',
                currency: data.currency || data.preferredCurrency || 'INR'
              });
            } else {
              // Set default values from Firebase Auth user
              setProfile({
                displayName: user.displayName || '',
                email: user.email || '',
                phone: user.phoneNumber || '',
                recoveryEmail: '',
                secondaryPhone: '',
                language: 'en-IN',
                timezone: 'Asia/Kolkata',
                currency: 'INR'
              });
            }
          } catch (settingsError) {
            console.error('Error loading from settings collection:', settingsError);
            // Set default values on error
            setProfile({
              displayName: user.displayName || '',
              email: user.email || '',
              phone: user.phoneNumber || '',
              recoveryEmail: '',
              secondaryPhone: '',
              language: 'en-IN',
              timezone: 'Asia/Kolkata',
              currency: 'INR'
            });
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        // Set default values on error
        setProfile({
          displayName: user.displayName || '',
          email: user.email || '',
          phone: user.phoneNumber || '',
          recoveryEmail: '',
          secondaryPhone: '',
          language: 'en-IN',
          timezone: 'Asia/Kolkata',
          currency: 'INR'
        });
      }
    };

    loadData();
  }, []);

  const handleProfileSave = async () => {
    if (!auth.currentUser) return;
    
    try {
      const normalizedPrimary = (profile.phone || '').trim();
      const e164 = normalizedPrimary ? (normalizedPrimary.startsWith('+') ? normalizedPrimary : `+91${normalizedPrimary}`) : '';

      const profileData = {
        displayName: profile.displayName,
        phone: normalizedPrimary,
        phoneNumber: normalizedPrimary,
        recoveryEmail: profile.recoveryEmail,
        secondaryPhone: profile.secondaryPhone,
        recoveryPhones: profile.secondaryPhone ? [profile.secondaryPhone] : [],
        language: profile.language,
        timezone: profile.timezone,
        currency: profile.currency,
        updatedAt: new Date()
      };

      // Save to main users collection first (primary location)
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, profileData);
      
      // Maintain phone_index mapping for primary phone -> { uid, email }
      try {
        const email = auth.currentUser?.email || '';
        if (e164 && email) {
          await setDoc(doc(db, 'phone_index', e164), { uid: auth.currentUser.uid, email, updatedAt: serverTimestamp() }, { merge: true });
        }
      } catch (_) {}
      
      // Also save to settings document as backup
      try {
        const settingsRef = getSettingsDoc(auth.currentUser.uid);
        await updateDoc(settingsRef, profileData);
      } catch (backupError) {
        console.warn('Failed to save to settings collection:', backupError);
        // Don't fail the whole operation if backup save fails
      }
      
      setProfileMsg('Profile updated successfully!');
      setIsEditingProfile(false);
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileMsg('Error updating profile. Please try again.');
    }
  };

  const handleProfileCancel = () => {
    setIsEditingProfile(false);
    setProfileMsg('');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600">Manage your personal information and account settings</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile Information
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' && (
          <>
            {/* Account Summary */}
            <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
              {account.photoURL ? (
                <img src={account.photoURL} alt="Avatar" className="w-16 h-16 rounded-full" />
              ) : (
                <span className="text-2xl text-gray-500">👤</span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-semibold text-gray-900">{account.displayName || 'User'}</h2>
                {account.emailVerified && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Verified
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600">{account.email}</div>
              <div className="text-xs text-gray-500 mt-1">
                <div>UID: {account.uid}</div>
                <div>Providers: {account.providers.join(', ') || '—'}</div>
                <div>Last sign-in: {account.lastSignInTime || '—'}{account.creationTime ? ` • Created: ${account.creationTime}` : ''}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Settings */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Profile Information</h3>
                  <p className="text-sm text-gray-600">Update your personal information and preferences.</p>
                </div>
                {!isEditingProfile ? (
                  <button 
                    onClick={() => setIsEditingProfile(true)} 
                    className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200"
                  >
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button 
                      onClick={handleProfileSave} 
                      className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button 
                      onClick={handleProfileCancel} 
                      className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input 
                    disabled={!isEditingProfile} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50" 
                    value={profile.displayName} 
                    onChange={(e) => setProfile({...profile, displayName: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Email</label>
                  <input 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100" 
                    value={account.email || ''} 
                    readOnly 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Contact</label>
                  <input 
                    disabled={!isEditingProfile} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50" 
                    value={profile.phone} 
                    onChange={(e) => setProfile({...profile, phone: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recovery Email</label>
                  <input 
                    disabled={!isEditingProfile} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50" 
                    value={profile.recoveryEmail} 
                    onChange={(e) => setProfile({...profile, recoveryEmail: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Contact</label>
                  <input 
                    disabled={!isEditingProfile} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50" 
                    value={profile.secondaryPhone} 
                    onChange={(e) => setProfile({...profile, secondaryPhone: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <AppSelect
                    disabled={!isEditingProfile}
                    value={profile.language}
                    onChange={(e) => {
                      const newLanguage = e.target.value;
                      setProfile({...profile, language: newLanguage});
                      changeLanguage(newLanguage);
                    }}
                    aria-label="Language"
                    sheetTitle="Language"
                    options={Object.entries(supportedLanguages).map(([code, info]) => ({
                      value: code,
                      label: `${info.flag} ${info.name} (${info.nativeName})`,
                    }))}
                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER} disabled:bg-gray-50`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <input 
                    disabled={!isEditingProfile} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50" 
                    value={profile.timezone} 
                    onChange={(e) => setProfile({...profile, timezone: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <input 
                    disabled={!isEditingProfile} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50" 
                    value={profile.currency} 
                    onChange={(e) => setProfile({...profile, currency: e.target.value})} 
                  />
                </div>
              </div>
              
              {profileMsg && (
                <div className={`text-sm ${profileMsg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {profileMsg}
                </div>
              )}
            </div>
          </div>
        </div>
          </>
        )}

      </div>
    </div>
  );
};

export default UserProfile;
